import React, { useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  ClickAwayListener,
  InputBase,
  List,
  Menu,
  MenuItem,
  Paper,
  Popper,
  useMediaQuery,
} from "@mui/material";
import { FolderItem } from "global/components/Folders/types";
import SearchResultRow from "global/components/SearchResultRow/SearchResultRow";
import EmptyState from "global/components/EmptyState/EmptyState";
import strings from "global/constants/StringConstants";
import filtersStyles from "../SignRequestsFilters.styles";
import { signRequestsResponsiveTokens } from "../SignRequests.tokens";
import {
  getSortLabel,
  SIGN_REQUESTS_SORT_OPTIONS,
  SignRequestsSortKey,
  SignRequestsView,
} from "../SignRequestsFiltersHelper";
import {
  CaretDownGlyph,
  CheckGlyph,
  SearchGlyph,
  SortGlyph,
} from "./SignRequestsIcons";
import SignRequestsViewsMenu from "./SignRequestsViewsMenu";

const styles = filtersStyles;
const { phone } = signRequestsResponsiveTokens;

const SEARCH_PLACEHOLDER = "Search requests by name, recipient, or status";

interface SignRequestsFiltersToolbarProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  searchAnchorRef: React.MutableRefObject<HTMLDivElement | null>;
  searchOpen: boolean;
  setSearchOpen: (value: boolean) => void;
  isSearching: boolean;
  searchResults: FolderItem[];
  resolveAncestorCrumbs: (item: FolderItem) => string[];
  onSearchSelect: (item: FolderItem) => void;
  sort: SignRequestsSortKey;
  onSortChange: (sort: SignRequestsSortKey) => void;
  views: SignRequestsView[];
  activeViewId: string | null;
  /** Saved views are still being read. */
  isLoadingViews: boolean;
  onSelectView: (view: SignRequestsView) => void;
  onSaveView: () => void;
  onRenameView: (view: SignRequestsView) => void;
  onDeleteView: (view: SignRequestsView) => void;
  onManageViews: () => void;
  disabled: boolean;
  /** Optional trailing controls per result row (the "more options" menu). */
  renderItemActions?: (item: FolderItem) => React.ReactNode;
  /** Optional inline marker per result row (a request's status chip). */
  renderItemBadge?: (item: FolderItem) => React.ReactNode;
}

/**
 * Search field plus the Sort and Views triggers above the list —
 * the toolbar of accounts with the advanced-filters feature. Everyone else
 * keeps `SignRequestsToolbar` and its status pills.
 *
 * The dropdown cards are the app's themed `Menu`; only the trigger chrome and
 * the rows' own layout come from the screen tokens.
 */
const SignRequestsFiltersToolbar = ({
  searchText,
  onSearchChange,
  searchAnchorRef,
  searchOpen,
  setSearchOpen,
  isSearching,
  searchResults,
  resolveAncestorCrumbs,
  onSearchSelect,
  sort,
  onSortChange,
  views,
  activeViewId,
  isLoadingViews,
  onSelectView,
  onSaveView,
  onRenameView,
  onDeleteView,
  onManageViews,
  disabled,
  renderItemActions,
  renderItemBadge,
}: SignRequestsFiltersToolbarProps) => {
  /*
   * A `placeholder` is an attribute, not a styled node, so the phone's shorter
   * prompt needs a media-query hook rather than a breakpoint value. Everything
   * else in this toolbar switches through `sx`.
   */
  const isPhone = useMediaQuery("@media (max-width:599.95px)");
  const placeholder = isPhone
    ? phone.search.placeholderShort
    : SEARCH_PLACEHOLDER;

  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);

  return (
    <Box sx={styles.toolbar}>
      <Box ref={searchAnchorRef} sx={styles.searchField}>
        <SearchGlyph sx={styles.searchGlyph} />
        <InputBase
          id="requests_search_box"
          type="search"
          placeholder={placeholder}
          value={searchText}
          onChange={(event) => onSearchChange(event.target.value)}
          disabled={disabled}
          sx={styles.searchInput}
          inputProps={{ "aria-label": SEARCH_PLACEHOLDER }}
        />
        {isSearching && (
          <CircularProgress
            size={16}
            role="status"
            aria-label="Searching requests"
          />
        )}
      </Box>

      <Popper
        open={searchOpen && !!searchAnchorRef.current}
        anchorEl={searchAnchorRef.current}
        placement="bottom-start"
        style={{
          zIndex: 1300,
          width: searchAnchorRef.current?.clientWidth,
        }}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            // A click inside a floating menu / popover (a result row's "more
            // options" menu is portaled to the body, outside this dropdown) is
            // not an outside click — ignore it so opening a row's menu does not
            // collapse the search dropdown out from under it.
            const target = event.target as HTMLElement | null;
            if (target?.closest?.(".MuiPopover-root")) return;
            setSearchOpen(false);
          }}
        >
          <Paper
            elevation={4}
            sx={{
              mt: 0.5,
              maxHeight: 360,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {searchResults.length === 0 ? (
              <EmptyState size="compact" title="No matches" />
            ) : (
              <List dense disablePadding>
                {searchResults.map((item) => {
                  const isFolder = item.type === strings.folder;
                  return (
                    <SearchResultRow
                      key={
                        isFolder
                          ? `f-${item.autoGeneratedId}`
                          : `r-${item.id ?? item.autoGeneratedId}`
                      }
                      isFolder={isFolder}
                      title={
                        isFolder
                          ? (item.templateName ?? "")
                          : (item.emailSubject ?? "")
                      }
                      crumbs={resolveAncestorCrumbs(item)}
                      createdBy={item.requestedBy?.name}
                      createdOn={item.createdOn ?? item.createdAt}
                      onClick={() => onSearchSelect(item)}
                      actions={renderItemActions?.(item)}
                      badge={renderItemBadge?.(item)}
                    />
                  );
                })}
              </List>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      <ButtonBase
        sx={styles.trigger}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={Boolean(sortAnchor)}
        aria-label={`Sort: ${getSortLabel(sort)}`}
        onClick={(event) => setSortAnchor(event.currentTarget)}
      >
        <SortGlyph sx={styles.triggerGlyph} />
        <Box component="span" sx={styles.triggerLabel}>
          {getSortLabel(sort)}
        </Box>
        <CaretDownGlyph sx={styles.triggerCaret} />
      </ButtonBase>
      <Menu
        anchorEl={sortAnchor}
        open={Boolean(sortAnchor)}
        onClose={() => setSortAnchor(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        PaperProps={{ sx: styles.sortMenuPaper }}
      >
        {SIGN_REQUESTS_SORT_OPTIONS.map((option) => {
          const active = option.key === sort;
          return (
            <MenuItem
              key={option.key}
              role="menuitemradio"
              aria-checked={active}
              sx={styles.menuItem(active)}
              onClick={() => {
                setSortAnchor(null);
                if (!active) onSortChange(option.key);
              }}
            >
              <Box component="span" sx={styles.menuCheckSlot}>
                {active && <CheckGlyph sx={styles.menuCheck} />}
              </Box>
              <Box component="span" sx={styles.menuItemLabel}>
                {option.label}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>

      <SignRequestsViewsMenu
        views={views}
        activeViewId={activeViewId}
        loading={isLoadingViews}
        disabled={disabled}
        onSelect={onSelectView}
        onSave={onSaveView}
        onRename={onRenameView}
        onDelete={onDeleteView}
        onManage={onManageViews}
      />
    </Box>
  );
};

export default SignRequestsFiltersToolbar;
